import * as React from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { ProcessesMaterializedItem, TasksTaskFragment } from 'generated/resources/graphql'

import SingleChoiceFilter from 'modules/core/components/Filter/filters/SingleChoice'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useGetOnboardingProcess } from 'modules/employee_onboarding/hooks/useGetOnboardingProcess'
import Chart from 'modules/engagement_insights/components/Chart'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ActionConfigurationIdentifiers, ItemStatuses } from 'modules/processes/domain'

import EmptyTasks from '../components/EmptyTasks'
import ItemsList from '../components/ItemsList'
import { useGetTasksProcess } from '../hooks/useGetTasksProcess'
import { useResolveTask } from '../hooks/useResolveTask'
import useTasksFilter, { Filter } from '../hooks/useTasksFilter'

const OnboardingTasksPage = () => {
  const [filterBy, setFilterBy] = React.useState<Filter>()

  const [actionsCompletedNumber, setActionsCompletedNumber] = React.useState<number>(0)
  const valid_actions = [ActionConfigurationIdentifiers.SendSurvey.toString()]

  const { access, employee } = useCurrentSession()

  const {
    materializedSteps,
    materializedProcess,
    loading: onboardingLoading,
    fetchOnboardingProcess,
  } = useGetOnboardingProcess()

  useEffect(() => {
    if (!employee?.id) return

    fetchOnboardingProcess({
      variables: {
        employeeId: employee?.id,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id])

  const materializedItems = (resource: string) => {
    return materializedSteps
      .flatMap((step) => step.materializedItems)
      .filter((item) => item.resourceType == resource)
  }

  const materializedTasks = materializedItems('Tasks::Task')
  const materializedActions = materializedItems('Processes::MaterializedAction')

  const actions = materializedActions.filter(
    (action) =>
      action.status === ItemStatuses.Completed &&
      valid_actions.includes(String(action.actionIdentifier))
  )
  const taskIds = [...new Set(materializedTasks.map((item) => +item.resourceId!))]

  const { tasks, loading: tasksLoading } = useGetTasksProcess({
    skip: !materializedProcess,
    assigneeId: access.id,
    ids: taskIds,
    materializedItems: materializedTasks as ProcessesMaterializedItem[],
  })

  const { allTasks, doneTasks, filteredTasks } = useTasksFilter(tasks, filterBy)

  const itemsCompleted = doneTasks.length + actionsCompletedNumber
  const itemNumber = allTasks.length + actions.length

  const completedPercentage = Math.ceil((itemsCompleted / itemNumber) * 100) || 0
  const completedText = `${itemsCompleted}/${itemNumber}`
  const currentStep = materializedSteps[materializedSteps.length - 1]
  const processCompleted = materializedProcess?.status === 'finalized'

  const { resolve } = useResolveTask({
    assigneeId: access.id,
    ids: taskIds,
  })

  const toggleTask = async (task: TasksTaskFragment) => {
    resolve({ id: task.id, done: !task.completedAt })
  }

  const isAnyTaskPending = useUnsafeMemo(() => {
    return tasks?.some((task) => !task.completedAt)
  }, [tasks])

  const titleText = processCompleted
    ? i18n.t('employees.onboarding_task.completed_onboarding_tasks')
    : isAnyTaskPending
    ? i18n.t('employees.onboarding_task.welcome_message')
    : i18n.t('employees.onboarding_task.completed_step_tasks', { step: currentStep?.name || '' })

  const descriptionText = isAnyTaskPending
    ? i18n.t('employees.onboarding_task.welcome_description')
    : i18n.t('employees.onboarding_task.upcomming_tasks', { step: currentStep?.name || '' })

  const loading = onboardingLoading || tasksLoading

  const listComponent =
    filteredTasks.length + actions.length ? (
      <>
        <ItemsList
          items={{ tasks: filteredTasks, actions: actions }}
          tasksLoading={loading}
          onToggle={toggleTask}
          setActionsCompletedNumber={setActionsCompletedNumber}
          isFiltering={!!filterBy}
        />
      </>
    ) : (
      <EmptyTasks filterBy={filterBy} clearFilter={() => setFilterBy(undefined)} />
    )

  return (
    <ResponsivePage.Body showHeader={false}>
      <Box flexDirection="row">
        <Box flexDirection="column" flexGrow>
          <Text.Header1>
            {i18n.t('employees.onboarding_task.welcome_name', { name: access.fullName })}
          </Text.Header1>
          <Text.Header1>{titleText}</Text.Header1>
          {!processCompleted && (
            <Box paddingTop="s12">
              <Text.Normal>{descriptionText}</Text.Normal>
            </Box>
          )}
        </Box>
        <Box
          flexDirection="column"
          gap="s16"
          border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
          borderRadius={{ all: 'abs006' }}
          paddingY="s16"
          paddingX="s12"
          width="s140"
        >
          <Text.Normal color="grey1000" weight="medium">
            {i18n.t('employees.onboarding_task.task_done')}
          </Text.Normal>
          <Box width="full" flexDirection="row" justifyContent="center">
            <Chart
              value={completedPercentage}
              valueCircleColor="#39B5BE"
              showScore
              valueText={completedText}
            />
          </Box>
        </Box>
      </Box>
      {!!allTasks.length && (
        <Box flexDirection="column" gap="s24">
          <Box
            flexDirection="row"
            paddingTop="s40"
            alignItems="center"
            justifyContent="spaceBetween"
          >
            <Box flexDirection="row" gap="s12">
              <Icon.Large icon="check" color="viridian1000" />
              <Text.Header1>{i18n.t('employees.onboarding_task.to_do')}</Text.Header1>
            </Box>
            <Box alignItems="flexEnd">
              <SingleChoiceFilter
                label={i18n.t('employees.onboarding_task.filter.label')}
                options={[
                  {
                    label: i18n.t('employees.onboarding_task.filter.options.due_today'),
                    value: 'due_today',
                  },
                  {
                    label: i18n.t('employees.onboarding_task.filter.options.overdue'),
                    value: 'overdue',
                  },
                  {
                    label: i18n.t('employees.onboarding_task.filter.options.mandatory'),
                    value: 'mandatory',
                  },
                  {
                    label: i18n.t('employees.onboarding_task.filter.options.optional'),
                    value: 'optional',
                  },
                ]}
                onValueChange={(selected) => setFilterBy(selected as Filter)}
                value={filterBy}
              />
            </Box>
          </Box>
          <Box>{listComponent}</Box>
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

export default OnboardingTasksPage
